/**
 * Street names for the path network (plan B's item, MAPGEN_DECISION.md §"what plan B got right" 5, built once the
 * edge key was pinned — §29).
 *
 * A name belongs to a **road**, not an edge: the run of one level's edges along one cell line, from where the line
 * starts being claimed to where it stops (`PathNet.roadKey`). Every edge of the run asks with the same key and gets
 * the same name; a name never depends on anything a neighbouring chunk would have to be asked for beyond the walk
 * along the line the net already caches.
 *
 * The words come from `content/names/*` lists of kind `street`, tagged `class_<cls>` for the kind of road and
 * `lang_<x>` for the language — a town speaks one language, hashed from the town, so its streets agree with each
 * other (the town's own name is drawn from a list at random today, which this cannot see; when it can, the language
 * should follow it). A trunk route is numbered, and the number belongs to the whole line, so the motorway keeps its
 * number through every town it passes.
 */
import type { Registry } from '../content/registry';
import type { NameList } from '../content/types';
import type { PathClass } from './paths';
import { hash01, hashStr, Rng } from './rng';
import type { Settlements } from './settlements';

export class StreetNames {
  private lists: NameList[];
  private langs: string[];
  private cache = new Map<string, string>();

  constructor(readonly reg: Registry, readonly seed: number, readonly settlements: Settlements) {
    this.lists = reg.all('namelist').filter((n) => n.kind === 'street' && (n.weight ?? 1) > 0);
    const langs = new Set<string>();
    for (const l of this.lists) for (const t of l.tags ?? []) if (t.startsWith('lang_')) langs.add(t);
    this.langs = [...langs].sort();
  }

  /** the language a place speaks: one per town, hashed from the town; the country between towns by region */
  private langAt(x: number, y: number): string | undefined {
    if (!this.langs.length) return undefined;
    let best: { cx: number; cy: number; d: number } | undefined;
    for (const s of this.settlements.near(x, y)) {
      const d = Math.hypot(x - s.x, y - s.y) / s.radius;
      if (d < 2.5 && (!best || d < best.d)) best = { cx: s.cx, cy: s.cy, d };
    }
    const [hx, hy] = best ? [best.cx, best.cy] : [Math.floor(x / 2048) * 1000003, Math.floor(y / 2048)];
    return this.langs[Math.floor(hash01(this.seed + 401, hx, hy) * this.langs.length) % this.langs.length];
  }

  /**
   * The name of a road. `key` is the road's identity (`PathNet.roadKey`), `cls` picks the list, (x, y) is where the
   * asking edge is — it decides the language. Deterministic in (seed, key, cls, language).
   */
  name(key: string, cls: PathClass, x: number, y: number): string {
    // cached by road *and* class: a run's class can change along it (a street becomes a lane past the edge of town),
    // and the name is drawn from the class's list — so the lane past the edge gets its own name, deterministically,
    // rather than whichever class happened to ask first
    // — and by language, judged where the asking edge is: a road that runs from one town into the next changes its
    // name at the boundary, which is what real roads do, and it keeps every answer a pure function of the place asked
    const lang = cls === 'highway' || cls === 'rail' ? undefined : this.langAt(x, y); // routes are numbered in every language
    const ck = `${key}:${cls}:${lang ?? ''}`;
    const hit = this.cache.get(ck);
    if (hit !== undefined) return hit;
    if (this.cache.size > 20000) this.cache.clear();
    const rng = new Rng(hashStr(`${key}:${cls}:${lang ?? ''}`, this.seed + 409));
    let name: string;
    if (cls === 'rail') name = 'Railway';
    else {
      const want = `class_${cls}`;
      let pool = this.lists.filter((l) => (l.tags ?? []).includes(want) && (!lang || (l.tags ?? []).includes(lang)));
      if (!pool.length) pool = this.lists.filter((l) => (l.tags ?? []).includes(want));
      if (!pool.length) pool = this.lists;
      const list = pool.length ? rng.weighted(pool, (l) => l.weight ?? 1) : undefined;
      name = list ? StreetNames.draw(list, rng) : cls === 'highway' ? 'Route' : 'Unnamed road';
      // a trunk route is numbered, and the number is the line's — every run of the same line shares it
      if (cls === 'highway' && !(list?.whole ?? []).includes(name)) name = `${name}${1 + Math.floor(hash01(this.seed + 419, StreetNames.lineOf(key), 0) * 98)}`;
    }
    this.cache.set(ck, name);
    return name;
  }

  /** the town-name rule, reused: a whole name sometimes, else prefix + suffix (a suffix carries its own space) */
  static draw(list: NameList, rng: Rng): string {
    const whole = list.whole ?? [], pre = list.prefixes ?? [], suf = list.suffixes ?? [];
    if (whole.length && (rng.chance(0.12) || !pre.length)) return rng.pick(whole);
    if (pre.length && suf.length) return rng.pick(pre) + rng.pick(suf);
    if (pre.length) return rng.pick(pre);
    return whole.length ? rng.pick(whole) : list.name;
  }

  /** the line part of a road key (`dir,line,level,from` → `dir,line,level`) as one number for hashing */
  static lineOf(key: string): number {
    const [dir, line, level] = key.split(',').map(Number);
    return (dir * 4 + level) * 1000003 + line;
  }
}
